'use client';

/* ============================================================================
 * PlatformPills - shared cross-platform fact rack.
 * ============================================================================
 * Reinforces the homepage manifesto ("In the browser, on iPad, on Android.")
 * and the footer "Runs on" row at the point a buyer is evaluating a specific
 * product. Type-first, no app-store badges.
 *
 * Offline-first, not a PWA: every device runs as a local replica of the
 * operational data and syncs to the server when the link is back. There is
 * no Service Worker and no install prompt. Native packaging is via Capacitor
 * for the mobile and tablet apps. See docs/humanization/OFFLINE_MODE.md for
 * the architecture this language is grounded in.
 *
 * Default list and "Runs on" eyebrow come from the same translation block
 * the V2 footer reads (`Layout.Footer.runsOn`), so the platform vocabulary
 * stays consistent everywhere it surfaces. Pass `items` to customise per
 * product. Pass `eyebrow={false}` to drop the "Runs on" label when the
 * surrounding context already provides one.
 * ========================================================================= */

import {useTranslations} from 'next-intl';

/** Identifier list used internally — keep order stable across locales. */
export const CANONICAL_PLATFORMS = [
  'Web',
  'iPad',
  'Android tablet',
  'Android phone',
  'Offline-first'
];

export default function PlatformPills({
  items,
  eyebrow = true,
  className = ''
}) {
  const t = useTranslations('Layout.Footer.runsOn');
  const labels = Array.isArray(items) ? items : t.raw('items');
  return (
    <div className={className}>
      {eyebrow && (
        <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-foreground-subtle">
          {t('label')}
        </p>
      )}
      <ul
        className={
          'flex flex-wrap items-center gap-2' + (eyebrow ? ' mt-3' : '')
        }
      >
        {labels.map((label) => (
          <li
            key={label}
            className="inline-flex items-center rounded-full border border-border bg-background-subtle px-3 py-1 text-[12px] font-medium text-foreground-muted"
          >
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
}
